"use client";

import * as React from "react";
import { ArrowRight, Heart, Star, ShoppingBag } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { AddToCartButton, EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const fashionMeta = [{
  badge: "TRENDING",
  brand: "LUSH SIGNATURE",
  spec: "Silk Blend | Summer Collection | Breathable",
  rating: 4.9,
  reviews: 128,
  hoverImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/fashion_silk_dress_hover.png",
  hoverSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/fashion_silk_dress_hover.png-wvc-srcset",
  hoverSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/fashion_silk_dress_hover.png-wvc-sizes"
}, {
  badge: "NEW",
  brand: "URBAN CHIC",
  spec: "Organic Cotton | Oversized Fit | Unisex",
  rating: 4.8,
  reviews: 94,
  hoverImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/fashion_cotton_tee_hover.webp",
  hoverSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/fashion_cotton_tee_hover.webp-wvc-srcset",
  hoverSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/fashion_cotton_tee_hover.webp-wvc-sizes"
}, {
  badge: "",
  brand: "LUSH DENIM",
  spec: "Vintage Wash | High Rise | Sustainable",
  rating: 4.7,
  reviews: 205,
  hoverImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/fashion_denim_jeans_hover.png",
  hoverSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/fashion_denim_jeans_hover.png-wvc-srcset",
  hoverSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/fashion_denim_jeans_hover.png-wvc-sizes"
}, {
  badge: "TRENDING",
  brand: "MODERN CUT",
  spec: "Wool Blend | Tailored Fit | Winter Ready",
  rating: 4.6,
  reviews: 156,
  hoverImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/fashion_wool_coat_hover.png",
  hoverSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/fashion_wool_coat_hover.png-wvc-srcset",
  hoverSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/fashion_wool_coat_hover.png-wvc-sizes"
}, {
  badge: "NEW",
  brand: "LUSH ACCESSORIES",
  spec: "Genuine Leather | Minimalist | Gold Hardware",
  rating: 5.0,
  reviews: 42,
  hoverImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/fashion_leather_bag_hover.webp",
  hoverSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/fashion_leather_bag_hover.webp-wvc-srcset",
  hoverSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/fashion_leather_bag_hover.webp-wvc-sizes"
}, {
  badge: "",
  brand: "CASUAL LUXE",
  spec: "Cashmere Blend | Soft Touch | Relaxed",
  rating: 4.5,
  reviews: 88,
  hoverImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/fashion_cashmere_knit_hover.png",
  hoverSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/fashion_cashmere_knit_hover.png-wvc-srcset",
  hoverSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/fashion_cashmere_knit_hover.png-wvc-sizes"
}];
function StarRating({
  rating,
  reviews
}: {
  rating: number;
  reviews: number;
}) {
  return <div className="flex items-center gap-1.5">
      <div className="flex items-center">
        {Array.from({
        length: 5
      }).map((_, i) => <Star key={i} data-index={i} className={i < Math.round(rating) ? "size-3.5 fill-[var(--chart-2)] text-[var(--chart-2)]" : "size-3.5 text-muted-foreground/40"} strokeWidth={1.5} />)}
      </div>
      <span className="font-mono text-xs text-muted-foreground">
        {rating.toFixed(1)} ({reviews})
      </span>
    </div>;
}
function WishlistConsumer() {
  const {
    products,
    loading,
    error,
    setSorting,
    refetch
  } = useEcommerceProducts();
  const [view, setView] = React.useState<"grid" | "list">("grid");
  const [sort, setSort] = React.useState<string>("popularity");
  const handleSortChange = (value: string) => {
    setSort(value);
    switch (value) {
      case "price_asc":
        setSorting("price", "asc");
        break;
      case "price_desc":
        setSorting("price", "desc");
        break;
      case "newest":
        setSorting("date", "desc");
        break;
      case "popularity":
      default:
        setSorting("date", "desc");
        break;
    }
  };
  if (loading) {
    return <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({
        length: 6
      }).map((_, i) => <div key={i} data-index={i} className="flex flex-col gap-4">
            <div className="aspect-[4/5] w-full animate-pulse bg-muted/60 rounded-lg" />
            <div className="h-4 w-2/3 animate-pulse bg-muted/60 rounded-lg" />
            <div className="h-3 w-1/3 animate-pulse bg-muted/60 rounded-lg" />
            <div className="h-3 w-1/2 animate-pulse bg-muted/60 rounded-lg" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="border border-border bg-card p-8 text-center rounded-lg">
        <p className="font-default text-sm text-muted-foreground">
          Unable to load your wishlist.{" "}
          <Button onClick={refetch} variant="link" className="h-auto p-0 font-default text-sm text-primary">
            Try again
          </Button>
        </p>
      </div>;
  }
  if (products.length === 0) {
    return <div className="border border-border bg-card p-12 text-center rounded-lg">
        <p className="font-default text-base text-muted-foreground">
          Your wishlist is currently empty. Start exploring our latest collections.
        </p>
      </div>;
  }
  return <>
      <div className="mb-12 flex flex-col items-start justify-between gap-6 border-y border-border py-5 md:flex-row md:items-center">
        <div className="flex items-center gap-3">
          <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
            Saved
          </span>
          <span className="font-default text-sm font-semibold text-foreground">
            {products.length} {products.length === 1 ? "item" : "items"}
          </span>
          <Badge variant="outline" className="ml-1 border-border font-mono text-[10px] uppercase tracking-wider text-muted-foreground rounded-md">
            Wishlist
          </Badge>
        </div>

        <div className="flex w-full flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:gap-4 md:w-auto">
          <Select value={sort} onValueChange={handleSortChange}>
            <SelectTrigger size="sm" className="w-full rounded-lg border-border bg-transparent font-mono text-xs uppercase tracking-wider sm:w-[200px]">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent className="rounded-lg border-border bg-popover">
              <SelectItem value="popularity" className="font-mono text-xs uppercase tracking-wider">
                Most Popular
              </SelectItem>
              <SelectItem value="newest" className="font-mono text-xs uppercase tracking-wider">
                Newest Arrivals
              </SelectItem>
              <SelectItem value="price_asc" className="font-mono text-xs uppercase tracking-wider">
                Price: Low to High
              </SelectItem>
              <SelectItem value="price_desc" className="font-mono text-xs uppercase tracking-wider">
                Price: High to Low
              </SelectItem>
            </SelectContent>
          </Select>

          <ToggleGroup type="single" value={view} onValueChange={v => v && setView(v as "grid" | "list")} variant="outline" size="sm" className="border-border rounded-lg">
            <ToggleGroupItem value="grid" aria-label="Grid view" className="font-mono text-xs uppercase tracking-wider data-[state=on]:bg-foreground data-[state=on]:text-background rounded-md">
              Grid
            </ToggleGroupItem>
            <ToggleGroupItem value="list" aria-label="List view" className="font-mono text-xs uppercase tracking-wider data-[state=on]:bg-foreground data-[state=on]:text-background rounded-md">
              List
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
      </div>

      <div className={view === "grid" ? "grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-3" : "flex flex-col gap-10"}>
        {products.map((product, i) => {
        const meta = fashionMeta[i % fashionMeta.length];
        return <article key={product.id} data-index={i} className="group relative flex flex-col">
              <Link to={product.link} className="block">
                <div className="relative aspect-[4/5] w-full overflow-hidden bg-muted rounded-lg shadow-md">
                  <img src={product.featured_image_url || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/fashion_placeholder.webp"} alt={product.title} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ease-out group-hover:opacity-0" />
                  <img src={meta.hoverImage} data-wvc-srcset={meta.hoverSrcset} data-wvc-sizes={meta.hoverSizes} alt={`${product.title} lifestyle view`} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover opacity-0 transition-opacity duration-700 ease-out group-hover:opacity-100" />

                  {meta.badge && <div className="absolute left-4 top-4">
                      <span className={meta.badge === "TRENDING" ? "inline-flex items-center bg-accent px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-widest text-accent-foreground rounded-md" : "inline-flex items-center bg-foreground px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-widest text-background rounded-md"}>
                        {meta.badge}
                      </span>
                    </div>}

                  <Button variant="ghost" size="icon" onClick={e => {
                e.preventDefault();
                e.stopPropagation();
              }} aria-label="Remove from wishlist" className="absolute right-4 top-4 size-9 bg-background/90 text-foreground backdrop-blur-sm hover:bg-background hover:text-destructive rounded-lg">
                    <Heart className="size-4 fill-current" strokeWidth={1.5} />
                  </Button>
                </div>
              </Link>

              <div className="mt-5 flex flex-1 flex-col">
                <Link to={product.link} className="block">
                  <h3 className="font-default text-xl font-semibold leading-tight text-foreground transition-colors hover:text-accent">
                    {product.title}
                  </h3>
                </Link>

                <p className="mt-1.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  {meta.brand}
                </p>

                <p className="mt-3 font-default text-sm text-muted-foreground">
                  {meta.spec}
                </p>

                <div className="mt-3">
                  <StarRating rating={meta.rating} reviews={meta.reviews} />
                </div>

                <div className="mt-4 flex items-baseline gap-2">
                  {product.discounted_price ? <>
                      <span className="font-default text-xl font-semibold text-foreground">
                        ${product.discounted_price}
                      </span>
                      <span className="font-mono text-sm text-muted-foreground line-through">
                        ${product.price}
                      </span>
                    </> : <span className="font-default text-xl font-semibold text-foreground">
                      ${product.price}
                    </span>}
                </div>

                <div className="mt-5 flex items-center justify-between gap-3 border-t border-border pt-4">
                  <Link to={product.link} className="group/link inline-flex items-center gap-1.5 font-mono text-xs font-semibold uppercase tracking-widest text-foreground transition-colors hover:text-accent">
                    View Details
                    <ArrowRight className="size-3.5 transition-transform group-hover/link:translate-x-1" strokeWidth={2} />
                  </Link>
                  <AddToCartButton productId={product.id} variant="default" size="sm" text="Add to Cart" showQuantity={false} buttonClassName="bg-foreground text-background rounded-lg hover:bg-accent hover:text-accent-foreground" />
                </div>
              </div>
            </article>;
      })}
      </div>
    </>;
}
export default function Wishlist() {
  const wp_query_params = {
    per_page: 6,
    product_cat: 16,
    orderby: "date",
    order: "desc" as const
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="441" id="savedproductswishlist" className="bg-background text-foreground py-28">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="mb-14 flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-end">
          <div className="max-w-2xl">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-accent">
              Your Favorites
            </p>
            <h2 className="mt-5 font-default text-5xl font-semibold leading-[1.05] tracking-tight text-foreground md:text-6xl">
              Saved Items
            </h2>
            <div className="mt-6 h-px w-16 bg-foreground/30" />
            <p className="mt-6 max-w-xl font-default text-base leading-relaxed text-muted-foreground">
              Curated pieces you've selected for your wardrobe. Keep track of your favorite styles and complete your look whenever you're ready.
            </p>
          </div>

          <Link to="/ShopPage" className="hidden lg:inline-flex">
            <Button variant="outline" className="group rounded-lg border-foreground bg-transparent font-mono text-xs font-semibold uppercase tracking-widest text-foreground hover:bg-foreground hover:text-background">
              Continue Shopping
              <ShoppingBag className="ml-2 size-3.5" />
            </Button>
          </Link>
        </div>

        <EcommerceProductsProvider wp_query={wp_query}>
          <WishlistConsumer />
        </EcommerceProductsProvider>

        <div className="mt-12 flex justify-center lg:hidden">
          <Link to="/ShopPage">
            <Button variant="outline" className="group rounded-lg border-foreground bg-transparent font-mono text-xs font-semibold uppercase tracking-widest text-foreground hover:bg-foreground hover:text-background">
              Continue Shopping
              <ShoppingBag className="ml-2 size-3.5" />
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}